import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { HiOutlineSearch, HiOutlineEmojiSad, HiOutlineLockClosed, HiOutlineUsers, HiOutlineLocationMarker, HiOutlineCalendar, HiOutlineLogin, HiOutlineShoppingBag, HiOutlineBan, HiOutlineCheckCircle } from 'react-icons/hi';
import AuthenticationService from '../service/AuthenticationService.js';
import AdminService from '../service/AdminService.js';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import Toast from '../components/Toast';
import '../css/admin.css'

const ROLES = [
    { value: 'USER', label: 'Customer', plural: 'Customers', tone: 'customer' },
    { value: 'MANAGER', label: 'Manager', plural: 'Managers', tone: 'manager' },
    { value: 'ADMIN', label: 'Admin', plural: 'Admins', tone: 'admin' }
]
const roleOf = (value) => ROLES.find(r => r.value === value) || { value, label: value || '–', tone: 'customer' }
const withArticle = (label) => (label === 'Admin' ? 'an ' : 'a ') + label

// What each role can do, shown when confirming a change.
const ROLE_HINTS = {
    USER: 'Customers can shop and see only their own orders.',
    MANAGER: 'Managers can run orders, deliveries and inventory, but cannot open Settings or change roles.',
    ADMIN: 'Admins can do everything, including Settings and changing roles.'
}

const STATUS_FILTERS = [
    { value: 'ALL', label: 'All' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'DISABLED', label: 'Disabled' }
]

// the API sends "dd MMM yyyy HH:mm"; the list shows the date part only
const shortDate = (value) => value ? String(value).split(' ').slice(0, 3).join(' ') : null

const initial = (name) => String(name || '?').trim().charAt(0).toUpperCase() || '?'

// Admin-only: everyone with an account, with role changes (User / Manager / Admin) and enable/disable.
const UserList = () => {
    const history = useHistory()
    const [me, setMe] = useState(null)
    const [userList, setUserList] = useState(null)
    const [loadError, setLoadError] = useState(false)
    const [search, setSearch] = useState('')
    const [roleFilter, setRoleFilter] = useState('ALL')
    const [statusFilter, setStatusFilter] = useState('ALL')
    const [pendingRole, setPendingRole] = useState(null)      // { user, role }
    const [pendingStatus, setPendingStatus] = useState(null)  // { user, status }
    const [isSaving, setIsSaving] = useState(false)
    const [actionError, setActionError] = useState('')
    const [toast, setToast] = useState('')

    // only admins (not managers) may open this page, change roles and enable/disable users
    const isAdminUser = me !== null && me.canManageSettings === true

    const loadUsers = useCallback(() => {
        setLoadError(false)
        AuthenticationService.getUserList().then(res => {
            setUserList(Array.isArray(res.data) ? res.data : [])
        }).catch(error => {
            console.log(error)
            setLoadError(true)
        })
    }, [])

    useEffect(() => {
        AuthenticationService.getUser().then(res => {
            setMe(res.data)
            if (res.data && res.data.canManageSettings === true) loadUsers()
        }).catch(error => {
            console.log(error)
            if (error?.response?.status === 401) history.push('/login')
            else setMe({ canManageSettings: false })
        })
    }, [history, loadUsers])

    const counts = useMemo(() => {
        const c = { ALL: 0, USER: 0, MANAGER: 0, ADMIN: 0, ACTIVE: 0, DISABLED: 0 }
        ;(userList || []).forEach(u => {
            c.ALL++
            if (c[u.role] !== undefined) c[u.role]++
            if (c[u.status] !== undefined) c[u.status]++
        })
        return c
    }, [userList])

    const visibleUsers = useMemo(() => {
        const q = search.trim().toLowerCase()
        return (userList || []).filter(u =>
            (roleFilter === 'ALL' || u.role === roleFilter) &&
            (statusFilter === 'ALL' || u.status === statusFilter) &&
            (!q || [u.name, u.email, u.phone].some(v => String(v || '').toLowerCase().includes(q))))
    }, [userList, search, roleFilter, statusFilter])

    const closeDialogs = () => {
        if (isSaving) return
        setPendingRole(null)
        setPendingStatus(null)
        setActionError('')
    }

    const confirmRoleChange = () => {
        const { user, role } = pendingRole
        setIsSaving(true)
        setActionError('')
        AdminService.changeUserRole(user.userId, role).then(res => {
            const newRole = (res.data && res.data.role) || role
            setUserList(list => list.map(u => u.userId === user.userId ? { ...u, role: newRole } : u))
            setIsSaving(false)
            setPendingRole(null)
            setToast(`${user.name} is now ${withArticle(roleOf(newRole).label)}`)
        }).catch(error => {
            setIsSaving(false)
            setActionError(error?.response?.data?.msg || 'Could not change the role. Please try again.')
        })
    }

    const confirmStatusChange = () => {
        const { user, status } = pendingStatus
        setIsSaving(true)
        setActionError('')
        AdminService.changeUserStatus(user.userId, status).then(res => {
            const data = res.data || {}
            setUserList(list => list.map(u => u.userId === user.userId
                ? { ...u, status: data.status || status, statusUpdatedAt: data.statusUpdatedAt, statusUpdatedBy: data.statusUpdatedBy }
                : u))
            setIsSaving(false)
            setPendingStatus(null)
            setToast(status === 'DISABLED' ? `${user.name} is disabled and signed out` : `${user.name} can sign in again`)
        }).catch(error => {
            setIsSaving(false)
            setActionError(error?.response?.data?.msg || 'Could not change the status. Please try again.')
        })
    }

    if (me === null) {
        return (
            <main className='mv-page' aria-busy='true'>
                <Skeleton width='160px' height={32} />
                <Skeleton height={44} radius='999px' style={{ marginTop: 20, maxWidth: 380 }} />
                {[0, 1, 2].map(i => <Skeleton key={i} height={88} radius='14px' style={{ marginTop: 12 }} />)}
            </main>
        )
    }

    if (!isAdminUser) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineLockClosed />} title='Only admins can manage users'
                    text='Ask an admin if you need to change someone’s role or access.'
                    actionLabel='Back to dashboard' onAction={() => history.push('/dashboard')} />
            </main>
        )
    }

    const roleFilters = [{ value: 'ALL', plural: 'All roles' }, ...ROLES.slice().reverse()]
    const clearFilters = () => { setSearch(''); setRoleFilter('ALL'); setStatusFilter('ALL') }

    return (
        <main className='mv-page mv-users'>
            <div className='mv-page-head'>
                <div>
                    <h1 className='mv-list-title'>Users {userList && <span className='mv-list-count'>{counts.ALL}</span>}</h1>
                    <p className='mv-page-sub'>Change roles, and disable or enable access. Disabling signs the person out and keeps all their data.</p>
                </div>
            </div>

            {loadError &&
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load users'
                    text='Please check your connection and try again.' actionLabel='Try again' onAction={loadUsers} />}

            {!loadError && <>
                <div className='mv-list-tools'>
                    <label className='mv-search'>
                        <HiOutlineSearch aria-hidden='true' />
                        <span className='mv-visually-hidden'>Search users</span>
                        <input type='search' className='mv-input' placeholder='Search by name, email or phone'
                            value={search} onChange={e => setSearch(e.target.value)} />
                    </label>
                    <div className='mv-user-filters'>
                        <div className='mv-chip-row' role='group' aria-label='Filter by role'>
                            {roleFilters.map(f =>
                                <button type='button' key={f.value} aria-pressed={roleFilter === f.value}
                                    className={roleFilter === f.value ? 'mv-filter-chip active' : 'mv-filter-chip'}
                                    onClick={() => setRoleFilter(f.value)}>
                                    {f.plural}{userList && <span className='mv-filter-count'>{counts[f.value]}</span>}
                                </button>
                            )}
                        </div>
                        <div className='mv-chip-row' role='group' aria-label='Filter by status'>
                            {STATUS_FILTERS.map(f =>
                                <button type='button' key={f.value} aria-pressed={statusFilter === f.value}
                                    className={statusFilter === f.value ? 'mv-filter-chip active' : 'mv-filter-chip'}
                                    onClick={() => setStatusFilter(f.value)}>
                                    {f.value === 'ALL' ? 'Any status' : f.label}{userList && <span className='mv-filter-count'>{counts[f.value]}</span>}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {!userList ?
                    <div className='mv-user-list' aria-busy='true'>
                        {[0, 1, 2, 3].map(i => <Skeleton key={i} height={88} radius='14px' />)}
                    </div>
                    : visibleUsers.length === 0 ?
                        <EmptyState icon={<HiOutlineUsers />}
                            title={userList.length === 0 ? 'No users yet' : 'No users match'}
                            text={userList.length === 0 ? 'People appear here once they create an account.' : 'Try another name, email, phone, role or status.'}
                            actionLabel={userList.length === 0 ? undefined : 'Clear filters'}
                            onAction={clearFilters} />
                        :
                        <div className='mv-user-list' role='table' aria-label='Users'>
                            <div className='mv-user-row mv-user-head' role='row'>
                                <span role='columnheader'>Name</span>
                                <span role='columnheader'>Role</span>
                                <span role='columnheader'>Location</span>
                                <span role='columnheader'>Orders</span>
                                <span role='columnheader'>Joined / last login</span>
                                <span role='columnheader'>Status</span>
                                <span role='columnheader' className='mv-visually-hidden'>Actions</span>
                            </div>
                            {visibleUsers.map(user => {
                                const isMe = user.userId === me.userId
                                const role = roleOf(user.role)
                                const isDisabled = user.status === 'DISABLED'
                                const location = [user.place, user.pincode].filter(Boolean).join(' · ')
                                const lastLogin = shortDate(user.lastLoginAt)
                                return (
                                    <div className={'mv-user-row' + (isMe ? ' is-me' : '') + (isDisabled ? ' is-disabled' : '')} role='row' key={user.userId}>
                                        <div className='mv-user-id' role='cell'>
                                            <span className={`mv-user-avatar mv-role-${role.tone}`} aria-hidden='true'>{initial(user.name)}</span>
                                            <span className='mv-user-name-wrap'>
                                                <span className='mv-user-name'>
                                                    {user.name}
                                                    {isMe && <span className='mv-chip mv-chip-you'>You</span>}
                                                    {!user.hasProfile && <span className='mv-chip mv-chip-incomplete' title='Has not added phone, address and PIN yet'>Profile incomplete</span>}
                                                </span>
                                                <span className='mv-user-email'>{user.email}</span>
                                                {user.phone && <span className='mv-user-email'>{user.phone}</span>}
                                            </span>
                                        </div>
                                        <div className='mv-user-role' role='cell'>
                                            {isMe ?
                                                <span className={`mv-chip mv-role-chip mv-role-${role.tone}`}>{role.label}</span> :
                                                <select className='mv-select mv-user-select' aria-label={`Role of ${user.name}`} value={user.role}
                                                    disabled={isSaving}
                                                    onChange={e => { setActionError(''); setPendingRole({ user, role: e.target.value }) }}>
                                                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                                </select>}
                                        </div>
                                        <div className='mv-user-meta mv-user-loc' role='cell'>
                                            <HiOutlineLocationMarker aria-hidden='true' /> <span className='mv-user-meta-label'>Location: </span>{location || '–'}
                                        </div>
                                        <div className='mv-user-meta mv-user-orders' role='cell'>
                                            <HiOutlineShoppingBag aria-hidden='true' /> <span className='mv-user-meta-label'>Orders: </span>{user.orderCount ?? 0}
                                        </div>
                                        <div className='mv-user-meta mv-user-dates' role='cell'>
                                            <span><HiOutlineCalendar aria-hidden='true' /> <span className='mv-user-meta-label'>Joined </span>{shortDate(user.createdAt) || '–'}</span>
                                            <span><HiOutlineLogin aria-hidden='true' /> {lastLogin ? 'Last login ' + lastLogin : 'Never logged in'}</span>
                                        </div>
                                        <div className='mv-user-status' role='cell'>
                                            <span className={'mv-chip ' + (isDisabled ? 'mv-status-disabled' : 'mv-status-active')}>
                                                {isDisabled ? 'Disabled' : 'Active'}
                                            </span>
                                            {user.statusUpdatedAt &&
                                                <span className='mv-user-status-note'>
                                                    {isDisabled ? 'Disabled' : 'Enabled'} {shortDate(user.statusUpdatedAt)}{user.statusUpdatedBy ? ' by ' + user.statusUpdatedBy : ''}
                                                </span>}
                                        </div>
                                        <div className='mv-user-actions' role='cell'>
                                            {!isMe && (isDisabled ?
                                                <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm' disabled={isSaving}
                                                    onClick={() => { setActionError(''); setPendingStatus({ user, status: 'ACTIVE' }) }}>
                                                    <HiOutlineCheckCircle aria-hidden='true' /> Enable
                                                </button> :
                                                <button type='button' className='mv-btn mv-btn-danger mv-btn-sm' disabled={isSaving}
                                                    onClick={() => { setActionError(''); setPendingStatus({ user, status: 'DISABLED' }) }}>
                                                    <HiOutlineBan aria-hidden='true' /> Disable
                                                </button>)}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                }
            </>}

            {pendingRole &&
                <Modal title={`Make ${pendingRole.user.name} ${withArticle(roleOf(pendingRole.role).label)}?`}
                    onClose={closeDialogs} busy={isSaving}
                    actions={<>
                        <button type='button' className='mv-btn mv-btn-secondary' onClick={closeDialogs} disabled={isSaving}>Cancel</button>
                        <button type='button' className={'mv-btn mv-btn-primary' + (isSaving ? ' is-busy' : '')}
                            onClick={confirmRoleChange} disabled={isSaving} aria-busy={isSaving}>
                            {isSaving ? 'Saving…' : 'Change role'}
                        </button>
                    </>}>
                    <p className='mv-modal-text'>
                        {roleOf(pendingRole.user.role).label} → <strong>{roleOf(pendingRole.role).label}</strong>. {ROLE_HINTS[pendingRole.role]}
                    </p>
                    {actionError && <div className='mv-alert mv-alert-error' role='alert'>{actionError}</div>}
                </Modal>}

            {pendingStatus &&
                <Modal title={pendingStatus.status === 'DISABLED' ? `Disable ${pendingStatus.user.name}?` : `Enable ${pendingStatus.user.name}?`}
                    onClose={closeDialogs} busy={isSaving}
                    actions={<>
                        <button type='button' className='mv-btn mv-btn-secondary' onClick={closeDialogs} disabled={isSaving}>Cancel</button>
                        <button type='button'
                            className={'mv-btn ' + (pendingStatus.status === 'DISABLED' ? 'mv-btn-danger-solid' : 'mv-btn-primary') + (isSaving ? ' is-busy' : '')}
                            onClick={confirmStatusChange} disabled={isSaving} aria-busy={isSaving}>
                            {isSaving ? 'Saving…' : pendingStatus.status === 'DISABLED' ? 'Disable' : 'Enable'}
                        </button>
                    </>}>
                    <p className='mv-modal-text'>
                        {pendingStatus.status === 'DISABLED'
                            ? `${pendingStatus.user.name} is signed out right away and can't use the app until you enable them. Nothing is deleted.`
                            : `${pendingStatus.user.name} can sign in again as ${withArticle(roleOf(pendingStatus.user.role).label)}.`}
                    </p>
                    {actionError && <div className='mv-alert mv-alert-error' role='alert'>{actionError}</div>}
                </Modal>}

            <Toast message={toast} onDone={() => setToast('')} />
        </main>
    )
}

export default UserList;
